import React, { useMemo, useState } from 'react';
import {
  AppButton,
  Box,
  List,
  PaginationBar,
  SearchPlaceholderIcon,
  SelectDropdown,
  Text,
} from '@common-fe/common-fe';

import { SORT_OPTIONS } from '@/common/constants';
import dropdownStateAllToggling, {
  checkIsAllOptionSelected,
} from '@/utils/handlers/dropdownStateAllToggling';

import ExcessContributionWithdrawModal from '../ExcessContributionWithdrawModal/ExcessContributionWithdrawModal';
import { useExcessContributionAvailable } from '../ExcessContributionWithdrawModal/useExcessContributionAvailable';
import { useGetEnrollments } from '../hooks/useGetEnrollment';

import useSearchParams from './hooks/useSearchParams';
import TaxFormsMessageQueueModalContainer from './TaxFormsMessageQueueModal/TaxFormsMessageQueueModal.container';
import { useTaxFormsListQuery } from './queries';
import { ITaxModalPayload } from './TaxForms.types';
import TaxFormsItem from './TaxFormsItem';
interface Props {
  employeeId?: string;
}
const TaxFormsContainer: React.FC<Props> = ({ employeeId }) => {
  const { isExcessContributionAvailable } = useExcessContributionAvailable(employeeId);
  const [currentTaxFormMessageQueue, setCurrentTaxFormMessageQueue] = useState<ITaxModalPayload | null>(null);
  const [isModalVisible, setModalVisible] = useState(false);
  const { yearOptions, page, perPage, setPerPage, setPage, sortParams, setSortParams, years, setYears } =
    useSearchParams();
  const yearsValues = useMemo(() => dropdownStateAllToggling(years), [years]);
  const { isActiveHSAExists } = useGetEnrollments({ enabled: true, employeeId });
  const isAllYearsSelected = useMemo(() => checkIsAllOptionSelected(years), [years]);
  const { total, isLoading, formattedData } = useTaxFormsListQuery({
    page: page - 1,
    perPage,
    sortBy: sortParams,
    years: isAllYearsSelected ? [] : yearsValues,
    employeeId,
  });

  return (
    <Box
      data-testid="tax-form_wrapper"
      direction="column"
      margin={{ bottom: 'l' }}
      pad="l"
      round="moduleRound"
      background="module"
    >
      <TaxFormsMessageQueueModalContainer
        currentTaxForm={currentTaxFormMessageQueue}
        onSetCurrentTaxForm={setCurrentTaxFormMessageQueue}
      />
      {isModalVisible ? <ExcessContributionWithdrawModal onSetVisible={setModalVisible} isEditMode /> : null}
      <Box direction="row" margin={{ bottom: 'l' }} align="center" justify="between">
        <Text size="large" weight={700}>
          {`Files: ${total}`}
        </Text>
        <Box direction="row">
          <Box margin={{ left: 'spacing12' }} width="180px">
            <SelectDropdown
              testId="tax-form_year"
              id="years"
              name="Years"
              prefix="Years"
              values={yearsValues}
              onChangeValues={setYears}
              options={yearOptions}
              activeTitle
              allMode
              ellipsisMode
            />
          </Box>
          <Box margin={{ left: 'spacing12' }} width="180px">
            <SelectDropdown
              id="sort_by"
              name="Sort by"
              testId="tax-form_sort"
              prefix="Sort by"
              value={sortParams}
              onChange={setSortParams}
              options={SORT_OPTIONS}
              activeTitle
              singleMode
            />
          </Box>
          {isExcessContributionAvailable ? (
            <Box margin={{ left: 'spacing24' }}>
              <AppButton
                testId="withdrawExcessContributions"
                width="250px"
                onClick={() => setModalVisible(true)}
                buttonType="secondary"
                disabled={!isActiveHSAExists}
              >
                Withdraw Excess Contributions
              </AppButton>
            </Box>
          ) : null}
        </Box>
      </Box>

      <List>
        <List.Header>
          <List.Header.Item>Form Name</List.Header.Item>
        </List.Header>
      </List>
      {isLoading ? (
        <List.Preloader count={3} />
      ) : (
        <>
          {!formattedData.length && (
            <Box
              direction="column"
              data-testid="tax-form_placeholder"
              pad={{ top: 'l', bottom: 'l' }}
              justify="center"
              align="center"
            >
              <SearchPlaceholderIcon size="72px" color="iconSecondary" />
              <Text weight={700} margin={{ top: 'l' }}>
                You have no tax forms for the tax year selected.
              </Text>
              <Text margin={{ top: 's' }}>
                'Forms are only created if there is spending or contribution activity during the tax year.'
              </Text>
              <Text>
                Form 1099-SA is available in January to report spending activity and 5498-SA in May to report
                any contributions made.
              </Text>
            </Box>
          )}
          {formattedData.map((item) => (
            <TaxFormsItem
              key={item.id}
              isActiveHSAExists={isActiveHSAExists}
              onOpenMailingQueue={setCurrentTaxFormMessageQueue}
              {...item}
            />
          ))}
        </>
      )}

      <PaginationBar
        testId="tax-forms_paginator"
        page={page}
        total={total}
        pageSize={perPage}
        onChangePage={setPage}
        onChangePageSize={setPerPage}
      />
    </Box>
  );
};

export default TaxFormsContainer;
